import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "I'm a plumber missing evening calls — can you help?",
  "How would an AI booking assistant work for me?",
  "What does this typically cost?",
];

const sessionId = (() => {
  try {
    const existing = window.localStorage.getItem("iariya_chat_session");
    if (existing) return existing;
    const s = `web_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
    window.localStorage.setItem("iariya_chat_session", s);
    return s;
  } catch {
    return `web_${Math.random().toString(36).slice(2, 10)}`;
  }
})();

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Hi, I'm iAriya 👋 — your friendly AI assistant from iAriya AI. Tell me about your business, or ask me anything about AI for trades. (Yes, I'm AI — try me!)",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
      });
      setMessages((m) => [
        ...m,
        { role: "assistant", content: res.data.reply || "(no reply)" },
      ]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            "Hmm, I couldn't reach our brain just now. Try again in a moment, or email eventinfo@iariya.com.",
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Toggle bubble */}
      {!open && (
        <button
          data-testid="chat-widget-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-6 z-50 group"
          aria-label="Open AI assistant"
        >
          <span className="absolute -top-10 right-0 bg-white border-2 border-ink rounded-full px-3 py-1 text-xs font-bold shadow-brutal whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
            Chat with iAriya AI
          </span>
          <span className="block h-16 w-16 rounded-full bg-[var(--pink)] border-2 border-ink shadow-brutal-lg flex items-center justify-center text-white hover:scale-105 transition-transform">
            <MessageCircle size={28} />
          </span>
          <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-[var(--yellow)] border-2 border-ink animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-20 right-6 z-50 w-[min(380px,calc(100vw-2rem))] h-[min(560px,calc(100vh-6rem))] bg-white border-2 border-ink rounded-3xl shadow-brutal-lg flex flex-col overflow-hidden pop-in"
        >
          {/* Header */}
          <div className="bg-[var(--pink)] text-white border-b-2 border-ink px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-white border-2 border-ink flex items-center justify-center text-[var(--pink)] font-display font-black">
                iA
              </div>
              <div>
                <p className="font-display text-lg font-extrabold leading-tight">
                  iAriya
                </p>
                <p className="text-xs text-white/85 flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-[var(--yellow)] inline-block" />
                  AI assistant · online
                </p>
              </div>
            </div>
            <button
              data-testid="chat-widget-close"
              onClick={() => setOpen(false)}
              className="h-9 w-9 rounded-full border-2 border-white/40 hover:bg-white/15 flex items-center justify-center"
              aria-label="Close chat"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chat-widget-messages"
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[var(--paper)]"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl border-2 border-ink text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[var(--blue)] text-white rounded-br-sm"
                      : "bg-white text-ink rounded-bl-sm"
                  }`}
                  data-testid={`chat-message-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="px-4 py-2.5 rounded-2xl border-2 border-ink bg-white text-ink text-sm flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> iAriya is
                  thinking…
                </div>
              </div>
            )}

            {messages.length <= 1 && !busy && (
              <div className="pt-2 space-y-2">
                <p className="text-xs font-bold uppercase tracking-wider text-stone-600">
                  Try asking
                </p>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid={`chat-suggestion-${SUGGESTIONS.indexOf(s)}`}
                    className="block w-full text-left text-sm border-2 border-ink rounded-2xl px-3 py-2 bg-[var(--yellow)] hover:bg-[var(--yellow)]/70 transition font-semibold"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t-2 border-ink p-3 bg-white">
            <div className="flex items-end gap-2">
              <textarea
                data-testid="chat-widget-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                rows={1}
                placeholder="Type a message…"
                className="flex-1 resize-none border-2 border-ink rounded-2xl px-3 py-2 text-sm focus:outline-none focus:bg-[var(--yellow)]/30 max-h-24"
              />
              <button
                data-testid="chat-widget-send"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                className="h-10 w-10 rounded-full bg-[var(--pink)] text-white border-2 border-ink flex items-center justify-center shadow-brutal disabled:opacity-50"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
            <p className="mt-2 text-[10px] text-stone-500 text-center">
              Demo powered by iAriya AI · Your business could have one of these.
            </p>
          </div>
        </div>
      )}
    </>
  );
};
